/**
 * Picking somebody to mention, while you type.
 *
 * Watches the word the caret is sitting in. When it starts with an @ the list
 * appears above the composer, arrow keys move through it, and Tab or Enter
 * completes the name - so mentioning somebody never means remembering how
 * they spell it.
 *
 * The candidates come from whoever is actually in this conversation, passed
 * in by the screen that owns it. A party offers its members; a direct message
 * offers the two of you.
 */

import { useEffect, useState } from 'react';
import type { PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { Avatar } from './Avatar';
import { UserName } from './user';

export type MentionState = {
  /** What has been typed after the @, lowercased. */
  query: string;
  /** Where the @ starts, so completing can replace exactly that run. */
  from: number;
};

/**
 * Finds the @word the caret is inside, if there is one.
 *
 * Anchored to a word boundary, so an email address does not open the list
 * halfway through typing it.
 */
export function mentionAt(value: string, caret: number): MentionState | null {
  const upto = value.slice(0, caret);
  const match = /(^|\s)@([a-z0-9_.-]{0,32})$/i.exec(upto);
  if (!match) return null;
  return {
    query: (match[2] ?? '').toLowerCase(),
    from: caret - (match[2] ?? '').length - 1
  };
}

export function MentionBox({ people, state, onPick, onClose }: {
  people: PublicUser[];
  state: MentionState;
  onPick: (user: PublicUser) => void;
  onClose: () => void;
}) {
  const [at, setAt] = useState(0);

  const matches = people
    .filter((u) =>
      u.username.toLowerCase().startsWith(state.query) ||
      u.displayName.toLowerCase().startsWith(state.query))
    .slice(0, 6);

  // A new query is a new list, so the highlight goes back to the top.
  useEffect(() => { setAt(0); }, [state.query]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!matches.length) return;
      if (e.key === 'ArrowDown') { e.preventDefault(); setAt((i) => (i + 1) % matches.length); }
      if (e.key === 'ArrowUp') { e.preventDefault(); setAt((i) => (i - 1 + matches.length) % matches.length); }
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      if (e.key === 'Enter' || e.key === 'Tab') {
        const pick = matches[at];
        if (pick) { e.preventDefault(); e.stopPropagation(); onPick(pick); }
      }
    }
    // Capture, so this runs before the composer's own Enter handling.
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [matches, at, onPick, onClose]);

  if (!matches.length) return null;

  return (
    <div
      data-keeps-card
      className="mb-1.5 overflow-hidden rounded-[var(--radius)] border border-line bg-surface-2 shadow-[var(--shadow-2)] animate-[rise_140ms_var(--ease-out-soft)]"
    >
      <div className="border-b border-line-soft px-3 py-1.5 text-[10.5px] font-bold tracking-[.08em] text-ghost uppercase">
        Mention someone
      </div>
      {matches.map((u, i) => (
        <button
          key={u.id}
          type="button"
          onMouseEnter={() => setAt(i)}
          onClick={() => onPick(u)}
          className={cn(
            'flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors',
            i === at ? 'bg-accent-soft' : 'hover:bg-surface-3'
          )}
        >
          <Avatar user={u} size={24} presence />
          <UserName user={u} className="truncate text-[13px] font-semibold" />
          <span className="ml-auto font-mono text-[11.5px] text-mute">@{u.username}</span>
        </button>
      ))}
    </div>
  );
}
